import { useCallback, useEffect, useRef } from 'react'

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

interface UseRecaptchaOptions {
  siteKey: string
  action?: string
}

export function useRecaptcha({ siteKey, action = 'contact_form' }: UseRecaptchaOptions) {
  const recaptchaSiteKey = siteKey
  const isLoaded = useRef(false)
  const isLoading = useRef(false)

  const loadRecaptcha = useCallback(() => {
    if (isLoaded.current || isLoading.current) return

    isLoading.current = true

    // Verifica se o script já foi carregado
    if (window.grecaptcha) {
      isLoaded.current = true
      isLoading.current = false
      return
    }

    // Carrega o script do reCAPTCHA
    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js?render=' + recaptchaSiteKey
    script.async = true
    script.defer = true
    script.onload = () => {
      isLoaded.current = true
      isLoading.current = false
    }
    script.onerror = () => {
      isLoading.current = false
      console.error('Erro ao carregar reCAPTCHA')
    }

    document.head.appendChild(script)
  }, [siteKey])

  const executeRecaptcha = useCallback(async (): Promise<string | null> => {
    if (!recaptchaSiteKey) {
      return null
    }

    if (!isLoaded.current) {
      loadRecaptcha()
      return null
    }

    if (!window.grecaptcha) {
      return null
    }

    try {
      return new Promise((resolve, reject) => {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(recaptchaSiteKey, { action })
            .then((token) => {
              resolve(token)
            })
            .catch((error) => {
              console.error('Erro ao executar reCAPTCHA:', error)
              reject(error)
            })
        })
      })
    } catch (error) {
      console.error('Erro ao executar reCAPTCHA:', error)
      return null
    }
  }, [recaptchaSiteKey, action, loadRecaptcha])

  useEffect(() => {
    loadRecaptcha()
  }, [loadRecaptcha])

  return {
    executeRecaptcha,
    isLoaded: isLoaded.current,
  }
}
